import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import isEmpty from 'lodash/isEmpty';

import StockChart from '../components/stockChart';
import ForwardCurve from '../components/forwardCurveChart';
import { IChartData, IChartDetails, IChartItem, IChartSettingsType, IFutureTable } from '../interface/chartImages.interface';
import '../styles/chartImageContainer.scss';
import { API_CONFIG, barChartFrequencyMapper } from '../../../shared/constants/api';
import HttpService from '../../../services/http.service';
import Spinner from '../../../shared/components/spinner/spinner';
import { IChartInformation } from '../../../shared/interface';
import ChartInformationTable from '../components/chartInformationTable';
import ChartTableInfo from '../components/chartInfo';
import { getDataTypeDisplayName } from '../../../shared/util/utility';

const ImageContainer = () => {
	const params = useParams();
	const [chartConfig, setChartConfig] = useState<any>();
	const [allChartDetails, setAllChartDetails] = useState({} as { [key: string]: IChartDetails });
	const [chartData, setChartData] = useState([] as IChartData[]);
	const [loading, setLoading] = useState(false);
	const [forwardCurveData, setForwardCurveData] = useState({} as { [key: string]: IFutureTable });
	const [information, setInformation] = useState<{ [key: string]: IChartInformation }>({});
	const [forecastKeys, setForecastKeys] = useState<string[]>([]);
	const [selectedFrequencies, setSelectedFrequencies] = useState<{ [materialKey: string]: string }>({});

	const getForwardCurveData = useCallback(
		async (chartConfig: any) => {
			const promises = chartConfig.chartSettings.map(async (chart: IChartSettingsType) => {
				const payload = {
					boerse: chart.boerse,
					symbol: chart.symbol,
					basis_symbol: chart.basis_symbol
				};
				try {
					const response = await HttpService.get(API_CONFIG.path.futuretable, payload, params.token as string);
					return {
						key: `${chart.symbol}-${chart.boerse}`,
						value: response
					};
				} catch (error) {
					console.error(error);
					return null;
				}
			});
			const results = await Promise.all(promises);
			const newForwardChartObject = results.reduce((acc: any, result) => {
				if (result) {
					acc[result.key] = result.value;
				}
				return acc;
			}, {});
			setForwardCurveData(newForwardChartObject);
		},
		[params.token]
	);

	const fetchChartData = useCallback(
		async (chartConfig: any) => {
			let payload: any = {
				data: chartConfig.chartSettings.map((chart: any) => {
					if (chart.fields && chart.fields.length > 0) {
						return chart.fields.map((field: string) => {
							return {
								id: chart.id,
								symbol: chart?.symbol,
								boerse: chart?.boerse,
								waehrung: chart.currency.value,
								unit: chart.unit?.value || '',
								year: parseInt(chart.history.value) || '',
								field: getDataTypeDisplayName(field)
							};
						});
					} else {
						// Default behavior when no fields are selected
						return {
							id: chart.id,
							symbol: chart?.symbol,
							boerse: chart?.boerse,
							waehrung: chart.currency.value,
							unit: chart.unit?.value || '',
							year: parseInt(chart.history.value) || ''
						};
					}
				})
				.flat(),
			};
			if (chartConfig.customChartSpan.bis !== '' && chartConfig.customChartSpan.von !== '') {
				payload = {
					...payload,
					end_date: chartConfig.customChartSpan.bis,
					start_date: chartConfig.customChartSpan.von
				};
			} else {
				payload = {
					...payload,
					span: chartConfig.chartType === 'bar' ? '1y' : chartConfig.span
				};
			}
			const response = await HttpService.post(API_CONFIG.path.getChartData, { ...payload },{}, params.token as string);

			// ✅ Clean data - remove bad cv... keys and their price/percentage counterparts
			const cleanedData = response.data.map((row: IChartData) => {
				const cleanedRow = { ...row };

				Object.keys(cleanedRow).forEach((key) => {
					if (key.startsWith('cv')) {
						const val = cleanedRow[key];

						const isEmptyObject = typeof val === 'object' && val !== null && Object.keys(val).length === 0;
						const allNull = typeof val === 'object' && val !== null && Object.values(val).every((v) => v === null);
						const noValidValue =
							typeof val === 'object' &&
							val !== null &&
							!['e', 'l', 't', 'o', 'h'].some((k) => {
								const item = val as IChartItem;
								const value = k === 'e' ? item.e : k === 'l' ? item.l : k === 't' ? item.t : k === 'o' ? item.o : item.h;
								return typeof value === 'number' && !isNaN(value);
							});

						if (val === null || isEmptyObject || allNull || noValidValue) {
							delete cleanedRow[key];

							const suffix = key.replace(/^cv/, '');
							delete cleanedRow[`price${suffix}`];
							delete cleanedRow[`percentage${suffix}`];
						}
					}
				});

				return cleanedRow;
			});

			setChartData(chartConfig.chartSettings.length > 1 ? cleanedData : response.data);
		const copyOfResponseHeaders = { ...response.mainHeader };
		payload.data.forEach((data: any, index: any) => {
			const key = `${data.symbol}-${data.boerse}-${index}`;
			if (copyOfResponseHeaders[key]) {
				copyOfResponseHeaders[key].chartPrice =
				copyOfResponseHeaders[key].chartPercentage =
					response.data[response.data.length - 1][`cv${data.symbol}-${data.boerse}-${index}`]?.e;
			}
		});
		setAllChartDetails(copyOfResponseHeaders);
		setLoading(false);
	},
	[params.token]
);

	const getInformationData = useCallback(
		async (chartConfig: any) => {
			try {
				const promises = chartConfig.chartSettings.map(async (chart: IChartSettingsType) => {
					const payload = {
						boerse: chart.boerse,
						symbol: chart.symbol
					};
					const response = await HttpService.get(API_CONFIG.path.chartInformation, payload, params.token as string);
					return {
						key: `${chart.symbol}-${chart.boerse}`,
						data: response
					};
				});

				const responses = await Promise.all(promises);
				// Create an object with material-specific keys
				const materialInformation = responses.reduce((acc, { key, data }) => {
					acc[key] = data;
					return acc;
				}, {} as { [key: string]: IChartInformation });

				setInformation(materialInformation);
			} catch (error) {
				console.error('Error fetching chart information:', error);
			}
		},
		[params.token]
	);

	const getChartConfig = useCallback(
		async (id: string) => {
			setLoading(true);
			HttpService.get(`${API_CONFIG.path.getChartConfig}/${id}`, {}, params.token as string)
				.then((response: any) => {
					setChartConfig(response.chartConfig);
					getForwardCurveData(response.chartConfig);
					fetchChartData(response.chartConfig);
					getInformationData(response.chartConfig);
					setForecastKeys(response.chartConfig.forecastKeys);
					// Set selectedFrequencies to courseGroup for all material keys
					const frequencies: { [key: string]: string } = {};
					Object.keys(response?.chartConfig?.selectedDataTypesPerMaterial || {}).forEach((key) => {
						frequencies[key] = barChartFrequencyMapper(response?.chartConfig?.courseGroup);
					});
					setSelectedFrequencies(frequencies);
				})
				.catch((err) => {
					console.error('err :>> ', err);
					setLoading(false);
				});
		},
		[fetchChartData, getForwardCurveData, getInformationData, params.token]
	);

	const getChartImages = useCallback(
		async (chartConfig: any) => {
			for (let i = 0; i < chartConfig.length; i++) {
				const image = await captureImage(`chart-container-history-${chartConfig[i].symbol}-${i + 1}`);
				const tempKey = `history-${chartConfig[i].symbol}-${chartConfig[i].boerse}-${chartConfig[i].history.value}-${chartConfig[i].unit.value}-${chartConfig[i].currency.value}`;
				const payload = {
					[tempKey]: image
				};
				HttpService.post(`${API_CONFIG.path.submitImage}/${params.id}`, payload,{}, params.token as string)
					.then()
					.catch();
			}
			for (let i = 0; i < chartConfig.length; i++) {
				const image = await captureImage(`chart-container-percentage-${chartConfig[i].symbol}-${i + 1}`);
				const tempKey = `percentage-${chartConfig[i].symbol}-${chartConfig[i].boerse}-${chartConfig[i].history.value}-${chartConfig[i].unit.value}-${chartConfig[i].currency.value}`;
				const payload = {
					[tempKey]: image
				};
				HttpService.post(`${API_CONFIG.path.submitImage}/${params.id}`, payload, {},params.token as string)
					.then()
					.catch();
			}
			const array = Object.keys(forwardCurveData);
			for (let i = 0; i < array.length; i++) {
				const image = await captureImage(`chart-container-forwardCurve-${array[i]}`);
				const tempKey = `forwardCurve-${array[i]}`;
				const payload = {
					[tempKey]: image
				};
				HttpService.post(`${API_CONFIG.path.submitImage}/${params.id}`, payload, {}, params.token as string)
					.then()
					.catch();
			}
			await updateCompletedFlag();
		},
		[forwardCurveData, params.id, params.token]
	);

	const updateCompletedFlag = () => {
		const payload = {
			isCompleted: 1
		};
	return 	HttpService.put(`${API_CONFIG.path.updateMasterData}/${params.id}`, payload, params.token as string)
			.then()
			.catch();
	};

	const captureImage = async (elementId: string) => {
		const element = document.getElementById(elementId);
		if (element) {
			const canvas = await html2canvas(element);
			return canvas.toDataURL();
		} else {
			return '';
		}
	};

	useEffect(() => {
		getChartConfig(params.id as string);
	}, [params.id]);

	useEffect(() => {
		if (!isEmpty(chartData) && !isEmpty(forwardCurveData) && !isEmpty(chartConfig)) {
			setTimeout(() => {
				getChartImages(chartConfig.chartSettings);
			}, 300);
		}
	}, [chartData, forwardCurveData, chartConfig, getChartImages]);

	const filteredChartData = useMemo(() => {
		// If isForeCastInclude is false, don't show any forecast data
		if (params.isForeCastInclude === 'false') {
			return chartData
				.map((dataPoint) => {
					const newDataPoint = { ...dataPoint };
					Object.keys(newDataPoint).forEach((key) => {
						const value = newDataPoint[key];
						if (
							key.startsWith('cv') &&
							value &&
							typeof value === 'object' &&
							!Array.isArray(value) &&
							'source_data' in value &&
							value.source_data &&
							typeof value.source_data === 'object'
						) {
							// For non-bar charts, remove all forecast data
							const newSourceData = { ...value.source_data };
							// Remove all forecast sources, keep only actual data
							Object.keys(newSourceData).forEach((sourceKey) => {
								if (sourceKey !== 'actual') {
									delete newSourceData[sourceKey];
								}
							});
							if (Object.keys(newSourceData).length === 0) {
								delete newDataPoint[key];
							} else {
								newDataPoint[key] = {
									...value,
									source_data: newSourceData
								};
							}
						}
					});
					return newDataPoint;
				})
				.filter((dataPoint) => {
					return Object.keys(dataPoint).some((key) => key.startsWith('cv'));
				});
		} else {
			// If isForeCastInclude is true or not specified, use forecastKeys logic
			if (!forecastKeys || forecastKeys.length === 0) {
				return chartData;
			}

			return chartData
				.map((dataPoint) => {
					const newDataPoint = { ...dataPoint };
					Object.keys(newDataPoint).forEach((key) => {
						const value = newDataPoint[key];
						if (
							key.startsWith('cv') &&
							value &&
							typeof value === 'object' &&
							!Array.isArray(value) &&
							'source_data' in value &&
							value.source_data &&
							typeof value.source_data === 'object'
						) {
							const newSourceData = { ...value.source_data };
							(forecastKeys || []).forEach((symbol: string) => {
								delete newSourceData[symbol];
							});
							if (Object.keys(newSourceData).length === 0) {
								delete newDataPoint[key];
							} else {
								newDataPoint[key] = {
									...value,
									source_data: newSourceData
								};
							}
						}
					});
					return newDataPoint;
				})
				.filter((dataPoint) => {
					return Object.keys(dataPoint).some((key) => key.startsWith('cv'));
				});
		}
	}, [chartData, forecastKeys, params.isForeCastInclude]);

	return (
		<div className='chart-images-container'>
			{isEmpty(chartData) && loading && <Spinner />}
			{chartConfig &&
				!loading &&
				!isEmpty(chartConfig.chartSettings) &&
				!isEmpty(chartData) &&
				chartConfig.chartSettings.map((ele: any, index: number) => {
					return (
						<Fragment>
							<div id={`chart-container-history-${ele.symbol}-${index + 1}`}>
								<StockChart
									key={index}
									advanceChartConfig={{
										...chartConfig,
										chartSettings: [
											{
												...ele,
												indicators: [],
												axis: {
													label: 'Right',
													value: 'right'
												}
											}
										],
										scaleType: 'price',
										selectedDataTypesPerMaterial: chartConfig.selectedDataTypesPerMaterial
									}}
									cardIndex={index + 1}
									chartData={filteredChartData}
									chartDetails={
										allChartDetails[
											`${chartConfig.chartSettings[index].symbol}-${chartConfig.chartSettings[index].boerse}-${index}`
										] as any
									}
									chartName='history'
									chartType='area'
									loading={false}
									showZoomButton={false}
									materialIndex={index}
									span={chartConfig.chartType === 'bar' ? '1y' : chartConfig.span}
									chartSettings={chartConfig.chartSettings}
									selectedFrequencies={selectedFrequencies}
								/>
								<div className='chart-info-table'>
									<ChartInformationTable
										information={
											information[`${chartConfig.chartSettings[index].symbol}-${chartConfig.chartSettings[index].boerse}`]
										}
										language={params.language || ''}
										length={chartConfig.chartSettings.length}
									/>
									{chartConfig.chartSettings.length >= 2 && (
										<ChartTableInfo
											chartData={chartData}
											chartDetails={
												allChartDetails[
													`${chartConfig.chartSettings[index].symbol}-${chartConfig.chartSettings[index].boerse}-${index}`
												] as any
											}
											span={chartConfig.span}
										/>
									)}
								</div>
							</div>
							<div id={`chart-container-percentage-${ele.symbol}-${index + 1}`}>
								<StockChart
									key={0}
									advanceChartConfig={{
										...chartConfig,
										chartSettings: [
											{
												...ele,
												indicators: [],
												axis: {
													label: 'Right',
													value: 'right'
												}
											}
										],
										scaleType: 'percentageChange',
										selectedDataTypesPerMaterial: chartConfig.selectedDataTypesPerMaterial
									}}
									cardIndex={index + 1}
									chartData={filteredChartData}
									chartDetails={
										allChartDetails[
											`${chartConfig.chartSettings[index].symbol}-${chartConfig.chartSettings[index].boerse}-${index}`
										] as any
									}
									chartName='percentage'
									chartType='line'
									loading={false}
									showZoomButton={false}
									materialIndex={index}
									span={chartConfig.chartType === 'bar' ? '1y' : chartConfig.span}
									chartSettings={chartConfig.chartSettings}
									selectedFrequencies={selectedFrequencies}
								/>
								<div className='chart-info-table'>
									<ChartInformationTable
										information={
											information[`${chartConfig.chartSettings[index].symbol}-${chartConfig.chartSettings[index].boerse}`]
										}
										language={params.language || ''}
										length={chartConfig.chartSettings.length}
									/>
									{chartConfig.chartSettings.length > 1 && (
										<ChartTableInfo
											chartData={chartData}
											chartDetails={
												allChartDetails[
													`${chartConfig.chartSettings[index].symbol}-${chartConfig.chartSettings[index].boerse}-${index}`
												] as any
											}
											span={chartConfig.span}
										/>
									)}
								</div>
							</div>
						</Fragment>
					);
				})}
			<div className='flex flex--wrap'>
				{chartConfig &&
					!loading &&
					!isEmpty(chartConfig.chartSettings) &&
					chartConfig.chartSettings.map((chart: any, index: number) => {
						return (
							<div>
								{!isEmpty(forwardCurveData) && !isEmpty(forwardCurveData[`${chart.symbol}-${chart.boerse}`]?.items) && (
									<>
										<ForwardCurve
											chartId={`chart-container-forwardCurve-${chart.symbol}-${chart.boerse}`}
											futureTableData={forwardCurveData[`${chart.symbol}-${chart.boerse}`]}
											information={information[`${chart.symbol}-${chart.boerse}`]}
											language={params.language || ''}
											chartDetails={
												allChartDetails[
													`${chartConfig.chartSettings[index].symbol}-${chartConfig.chartSettings[index].boerse}-${index}`
												] as any
											}
											chartConfigLength={chartConfig.chartSettings.length}
											span={chartConfig.chartType === 'bar' ? '1y' : chartConfig.span}
										/>
									</>
								)}
							</div>
						);
					})}
			</div>
		</div>
	);
};

export default ImageContainer;
